import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react'
import { ClientAnalytics } from '@/modules/clientAnalytics'

// Theme definitions - simplified to just names and display info
const themes = {
  system: {
    name: 'System',
    icon: '🖥️',
    description: 'Follow system preference'
  },
  light: {
    name: 'Light',
    icon: '☀️',
    description: 'Clean, bright interface'
  },
  dark: {
    name: 'Dark',
    icon: '🌙',
    description: 'Easy on the eyes for nightly work'
  }
} as const

export type ThemeName = keyof typeof themes
export type Theme = typeof themes[ThemeName]

interface ThemeContextType {
  currentTheme: Theme
  themeName: ThemeName
  isDark: boolean
  setTheme: (theme: ThemeName) => void
  availableThemes: ThemeName[]
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

const isSystemDark = () => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: ThemeName
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children
}) => {
  const [noPick, setNoPick] = useState<boolean>(false)
  const [themeName, setThemeName] = useState<ThemeName>('system')
  const [themeLoaded, setThemeLoaded] = useState<boolean>(false)

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeName
    if (savedTheme && themes[savedTheme]) {
      setNoPick(false)
      setThemeName(savedTheme)
    } else {
      setNoPick(true)
      setThemeName(isSystemDark() ? 'dark' : 'light')
    }
    setThemeLoaded(true)
  }, [])

  useLayoutEffect(() => {
    const root = document.documentElement
    root.className = root.className.replace(/theme-\w+/g, '')

    if (themeName) {
      const pickedTheme = themeName === 'system' ? (isSystemDark() ? 'dark' : 'light') : themeName
      root.classList.add(`theme-${pickedTheme}`)

      // Add 'dark' class for Tailwind dark mode support
      if (pickedTheme === 'dark') {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }
    }
  }, [themeName, noPick])

  useEffect(() => {
    if (!themeLoaded) return
    ClientAnalytics.setUserProperties({ Theme: themeName })
  }, [themeName, themeLoaded])

  const setTheme = (theme: ThemeName) => {
    localStorage.setItem('theme', theme)
    setNoPick(false)
    setThemeName(theme)
  }

  const value: ThemeContextType = {
    currentTheme: themes[themeName],
    themeName,
    isDark: themeName === 'dark' || (themeName === 'system' && isSystemDark()),
    setTheme,
    availableThemes: Object.keys(themes) as ThemeName[]
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
} 